import React from "react";
import PropTypes from "prop-types";
import { TimerPopup } from "./TimerPopup";
import { TimerMain } from "./TimerImages";
import { TimerControl } from "./TimerControl";
import className from "./style.module.scss";
import {
  splitTime,
  composeTime,
  timerFinished,
  EMPTY_TIME,
} from "./Timer.utils";

import { I18nextProvider } from "react-i18next";
import i18next from "./locales/i18next";

const i18n = i18next;

const changeLang = (lang) => {
  i18n.changeLanguage(lang);
};

let timerRunInterval = null;
/**
 * Timer:
 *   Lab Equipment with a timer; You can set a particular timer and make the timer run
 *   down the set time.
 */
export class Timer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      time: this.props.time,
      timePassed: 0,
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.time !== this.props.time) {
      this.setState({ time: this.props.time });
    }
    if (this.props.lang && this.props.lang !== prevProps.lang) {
      changeLang(this.props.lang);
    }
  }

  componentDidMount() {
    if (this.props.lang) {
      changeLang(this.props.lang);
    }
  }

  stopTimer = () => {
    clearInterval(timerRunInterval);
    this.setState(
      {
        timePassed: this.state.timePassed + (this.props.time - this.state.time),
      },
      () => {
        this.callBack(this.state.timePassed);
        if (this.state.time === EMPTY_TIME) {
          this.setState({
            timePassed: 0,
          });
        }
      }
    );
    this.props.setTime(this.state.time);
    this.props.setRunning(false);
  };

  forceStopTimer = () => {
    clearInterval(timerRunInterval);
    this.props.setTime(EMPTY_TIME);
    this.setState(
      {
        time: EMPTY_TIME,
        timePassed: this.state.timePassed + (this.props.time - EMPTY_TIME),
      },
      () => {
        this.callBack(this.state.timePassed);
        if (this.state.time === EMPTY_TIME) {
          this.setState({
            timePassed: 0,
          });
        }
      }
    );
    this.props.setRunning(false);
  };

  resetTimer = () => {
    if (timerRunInterval) clearInterval(timerRunInterval);
    if (this.props.running) this.props.setRunning(false);
    this.props.setTime(EMPTY_TIME);
    this.setState({
      time: EMPTY_TIME,
      timePassed: 0,
    });
  };

  // pauseTimer = () => {
  //   if (timerRunInterval) clearInterval(timerRunInterval);
  //   if (this.props.running) this.props.setRunning(false);
  //   //this.props.setTime(EMPTY_TIME);
  //   //this.setState({time: EMPTY_TIME});
  // }

  callBack = (timePassed) => {
    if (timePassed <= this.props.callback.amount) {
      this.props.callback.function(timePassed);
    }
  };

  runTimer = () => {
    const [h, m, s] = splitTime(this.state.time);
    this.setState(
      {
        time: composeTime(h, m, s - 10),
      },
      () => {
        timerFinished(this.state.time) && this.stopTimer();
      }
    );
  };

  startTimer = () => {
    this.props.setRunning(true);
    if (timerRunInterval) clearInterval(timerRunInterval);
    timerRunInterval = setInterval(
      this.runTimer,
      this.props.defaultTimeUpdateRate
    );
  };

  openPopup = (event) => {
    event.stopPropagation();
    this.props.setShowPopup(true);
  };

  closePopup = (event) => {
    event.stopPropagation();
    this.props.setShowPopup(false);
  };

  getDisplayTime = () =>
    this.props.running ? this.state.time : this.props.time;

  getFooterButtonCallback = () => {
    if (this.props.running) {
      return this.forceStopTimer;
    } else {
      return this.props.onClickDone;
    }
  };

  render() {
    return (
      <I18nextProvider i18n={i18n}>
        <div className={className.timer}>
          <div
            role="button"
            tabIndex="0"
            onClick={this.openPopup}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                this.openPopup;
              }
            }}
          >
            <TimerMain className={className.labIcon} />
          </div>
          <TimerPopup
            onClose={this.closePopup}
            isOpen={this.props.showPopup}
            skipTimer={this.props.running}
            onFooterClick={this.getFooterButtonCallback()}
            time={this.getDisplayTime()}
            currentlySetTime={this.props.time}
            className={className.modal}
            notebookExpanded={this.props.notebookExpanded}
            startTimer={this.startTimer}
            stopTimer={this.stopTimer}
            setTime={(time) => {
              this.setState({ timePassed: 0 });
              this.props.setTime(time);
            }}
            running={this.props.running}
            resetTimer={this.resetTimer}
            //pauseTimer={this.pauseTimer}
          >
            <TimerControl
              time={this.getDisplayTime()}
              setTime={(time) => {
                this.setState({ timePassed: 0 });
                this.props.setTime(time);
              }}
              startTimer={this.startTimer}
              stopTimer={this.stopTimer}
              disabled={this.props.running}
            />
          </TimerPopup>
        </div>
      </I18nextProvider>
    );
  }
}

Timer.propTypes = {
  /**
   * Boolean to set if popup should be displayed
   */
  showPopup: PropTypes.bool,
  /**
   * Function to set/reset popup display
   */
  setShowPopup: PropTypes.func,
  /**
   * Boolean to set if timer is running
   */
  running: PropTypes.bool,
  /**
   * Function to set/reset running
   */
  setRunning: PropTypes.func,
  /**
   * time to display on timer
   */
  time: PropTypes.number,
  /**
   * Fuction to update Time
   */
  setTime: PropTypes.func,
  /**
   * Callback for Done button in Footer
   */
  onClickDone: PropTypes.func,
  /**
   * specifies code of language the component is to be rendererd
   */
  lang: PropTypes.oneOf([
    "en",
    "ar",
    "ja",
    "nl",
    "de",
    "es",
    "fr",
    "it",
    "ko",
    "pt",
    "tr",
    "zh",
    "zu",
  ]),
};

Timer.defaultProps = {
  defaultTimeUpdateRate: 20,
};

/**
 * Used class Component because setRunning(false) and setTime(time)
 * in functional components needs to run in parallel with setTime() [stat update operation]
 * This was giving render warnings
 */
